import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NavController, Platform } from '@ionic/angular';

import makeDebug from '../../../../makeDebug';
import { AuthService } from '../../services/auth.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { RootHistorySetter as History } from '../contracts/sync/sync-endpoints';
import { TrackerService } from '@services/tracker.service';

const debug = makeDebug('sync:root-history-setter');
const LOGIN_ROUTE = '/login';

/**
 * Sets the mobile app's root page once the sync pipeline has authenticated the user.
 *
 * Runs on every pipeline run — it used to flag itself as execute-only-once. The first run sets the
 * root to the post-login target as before. Later runs only act when the user was pushed to /login in
 * the meantime (the pipeline sends them there whenever an authentication attempt fails) and bring
 * them back in once an attempt has succeeded again. The login page does the same on the
 * authenticated *transition*; this element covers the runs in which the flag never flipped, e.g.
 * when only the ionic-appauth session had been missing (#26615).
 */
@Injectable({ providedIn: 'root' })
export class RootHistorySetter implements ISyncElement {
  private _hasNavigated = false;

  public get name(): string {
    return History;
  }

  constructor(
    private _navController: NavController,
    private _authService: AuthService,
    private _ngZone: NgZone,
    private _platform: Platform,
    private _trackerService: TrackerService,
    private _router: Router
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered RootHistorySetter execute function');
    const elementResult: SyncElementResult = { contextId: context.id, jobDone: true, hasTarget: false };

    if (!this._platform.is('hybrid')) {
      elementResult.nativePlatform = false;
      return elementResult;
    }
    elementResult.nativePlatform = true;

    if (this._hasNavigated) {
      if (!this.isOnLoginPage()) {
        debug('root already set and the user is inside the app, nothing to do');
        return elementResult;
      }
      // never start a navigation the route guards would refuse: AuthGuard would send the user
      // straight back to /login, and the next run would try the same again
      if (!this._authService.canEnterApp) {
        window.logger.addBreadcrumb({
          message: '[RootHistorySetter] user on /login, but the route guard would refuse — staying',
        });
        return elementResult;
      }
      window.logger.addBreadcrumb({
        message: '[RootHistorySetter] user on /login after a successful authentication, bringing them back in',
      });
    }

    const navigationTarget = this._authService.resolvePostLoginTarget();
    await this._ngZone.run(() => this._navController.navigateRoot(navigationTarget));
    this._hasNavigated = true;
    this._trackerService.debugSyncPipelineRootHistorySetter(navigationTarget);

    elementResult.hasTarget = true;
    elementResult.navigationTarget = navigationTarget;
    return elementResult;
  }

  private isOnLoginPage(): boolean {
    return (this._router.url ?? '').startsWith(LOGIN_ROUTE);
  }
}
